import type { ReactNode } from 'react';
import { EntityRefLink, entityRouteRef } from '@backstage/plugin-catalog-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import {
  assetCandidateRefs,
  pickExistingRef,
  toGroupRef,
} from '../utils/entityRefHelpers';
import { nvdUrl } from '../utils/cveHelpers';

/**
 * Renders a security-insights asset value, as a clickable entity link when
 * the catalog has a matching entity and as plain text otherwise.
 *
 * `issueType` lets `assetCandidateRefs` narrow the candidate kinds — GHAS
 * findings only check `component`, others skip it. `stopPropagation` is
 * needed in tables whose rows have their own `onClick` handler (e.g. the
 * by-asset page's expand-on-row-click), so link clicks don't bubble up
 * and trigger row selection.
 */
export const AssetCell = ({
  asset,
  issueType,
  existingRefs,
  stopPropagation,
}: {
  asset: string | null | undefined;
  issueType?: string;
  existingRefs: ReadonlySet<string>;
  stopPropagation?: boolean;
}): ReactNode => {
  const ref = pickExistingRef(
    assetCandidateRefs(asset ?? '', issueType),
    existingRefs,
  );
  if (!ref) return <>{asset}</>;
  return stopPropagation ? (
    <EntityRefLink entityRef={ref} onClick={e => e.stopPropagation()} />
  ) : (
    <EntityRefLink entityRef={ref} />
  );
};

/**
 * Renders a security-insights team value, as a clickable group link when
 * the catalog has a matching `group:default/<team>` entity and as plain
 * text otherwise (covers the `unowned` sentinel and any team name without
 * a catalog entry).
 *
 * Unlike a plain `EntityRefLink` (which resolves to the group's base
 * catalog route → Overview tab), this deep-links into the group's
 * `/security` tab so the team's Security dashboard opens directly. The path
 * is built from `useRouteRef(entityRouteRef)` (the base catalog entity
 * route) plus a `/security` suffix that matches `groupPage`'s
 * `<EntityLayout.Route path="/security">` in `EntityPage.tsx`.
 */
export const TeamCell = ({
  team,
  existingRefs,
  stopPropagation,
}: {
  team: string | null | undefined;
  existingRefs: ReadonlySet<string>;
  stopPropagation?: boolean;
}): ReactNode => {
  const entityRoute = useRouteRef(entityRouteRef);
  const ref = toGroupRef(team ?? '');
  if (!ref || !existingRefs.has(ref)) return <>{team}</>;
  const to = `${entityRoute({
    kind: 'group',
    namespace: 'default',
    name: team as string,
  })}/security`;
  return (
    <Link
      to={to}
      onClick={stopPropagation ? e => e.stopPropagation() : undefined}
    >
      {team}
    </Link>
  );
};

/**
 * Renders a `cve_or_rule` value, as a link to its NVD detail page when the
 * value is a CVE ID and as plain text otherwise (scanner rule names, or —
 * on the License Compliance page, where this field holds license
 * identifiers — plain license strings). Trims once so the displayed text
 * and the linked URL (built from the same trimmed string) never diverge.
 *
 * `stopPropagation` is needed in tables whose rows have their own
 * `onClick` handler (e.g. the scorecard issues table's row-click-to-open
 * drawer), so the NVD link doesn't also trigger row selection.
 */
export const CveCell = ({
  value,
  stopPropagation,
}: {
  value: string | null | undefined;
  stopPropagation?: boolean;
}): ReactNode => {
  const text = (value ?? '').trim();
  if (!text) return <>—</>;
  const url = nvdUrl(text);
  if (!url) return <>{text}</>;
  return (
    <Link
      to={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stopPropagation ? e => e.stopPropagation() : undefined}
    >
      {text}
    </Link>
  );
};

/**
 * Renders a finding's taxonomy placement as "Pillar / Category".
 *
 * Takes code→label maps rather than labels because the findings columns store
 * codes; the vocabulary comes from `getPillars()`/`getCategories()`. An
 * unmapped code (retired via `deprecated`, or a row classified by a newer
 * taxonomy version than the UI has fetched) falls back to showing the raw
 * code — visibly odd rather than silently blank.
 *
 * The category line is suppressed when it repeats the pillar label: pillars
 * I and II (Vulnerability, Attack Paths) each have a single same-named
 * category, which would otherwise print the same string twice on every row.
 */
export const TaxonomyCell = ({
  pillar,
  category,
  pillarLabels,
  categoryLabels,
}: {
  pillar?: string | null;
  category?: string | null;
  pillarLabels: ReadonlyMap<string, string>;
  categoryLabels: ReadonlyMap<string, string>;
}): ReactNode => {
  if (!pillar) {
    return <span style={{ opacity: 0.6 }}>Unclassified</span>;
  }
  const pillarLabel = pillarLabels.get(pillar) ?? pillar;
  const categoryLabel = category
    ? (categoryLabels.get(category) ?? category)
    : null;
  return (
    <>
      <div>{pillarLabel}</div>
      {categoryLabel && categoryLabel !== pillarLabel && (
        <div style={{ fontSize: '0.8em', opacity: 0.7 }}>{categoryLabel}</div>
      )}
    </>
  );
};
